/**
 * Controller Stimulus para autopreenchimento de endereço usando a API ViaCEP
 * 
 * Este controller permite o preenchimento automático de campos de endereço a partir
 * de um CEP informado. Ele suporta tanto o formato padrão do Rails quanto campos
 * personalizados através de data-attributes.
 * 
 * @example Uso básico com Simple Form
 * ```erb
 * <%= f.input :zip_code, 
 *     input_html: {
 *       data: {
 *         controller: "viacep input-mask",
 *         viacep_target: "zipCode",
 *         input_mask_mask_value: "99999-999"
 *       }
 *     } %>
 * <%= f.input :address %>
 * <%= f.input :neighborhood %>
 * <%= f.input :city %>
 * <%= f.input :state %>
 * ```
 * 
 * @example Uso com campos personalizados
 * ```erb
 * <input type="text" 
 *        data-controller="viacep"
 *        data-viacep-target="zipCode"
 *        data-action="blur->viacep#fetchAddress" />
 * 
 * <input type="text" 
 *        data-viacep-field="address" 
 *        placeholder="Endereço" />
 * 
 * <input type="text" 
 *        data-viacep-field="neighborhood" 
 *        placeholder="Bairro" />
 * 
 * <input type="text" 
 *        data-viacep-field="city" 
 *        placeholder="Cidade" />
 * 
 * <input type="text" 
 *        data-viacep-field="state" 
 *        placeholder="Estado" />
 * ```
 * 
 * @example Uso com nomes de campos diferentes
 * ```erb
 * <%= f.input :postal_code,  # Campo do CEP com nome diferente
 *     input_html: {
 *       data: {
 *         controller: "viacep",
 *         viacep_target: "zipCode"
 *       }
 *     } %>
 * 
 * <%= f.input :street,       # Campo com nome diferente do padrão
 *     input_html: {
 *       data: { viacep_field: "address" }
 *     } %>
 * 
 * <%= f.input :district,     # Campo com nome diferente do padrão
 *     input_html: {
 *       data: { viacep_field: "neighborhood" }
 *     } %>
 * ```
 */
import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="viacep"
export default class extends Controller {
  /** @type {Array<String>} - Define o target do campo de CEP */
  static targets = ["zipCode"]

  /**
   * Callback chamado quando o controller é conectado ao elemento
   * Inicializa os eventos de blur e input no campo de CEP
   */
  connect() {
    // Verifica se está no contexto de um formulário
    const form = this.element.closest('form')
    
    if (form) {
      // Lista todos os campos do formulário para debug
      const allFields = form.querySelectorAll('input, textarea, select')
      
      const addressField = this.findFieldInForm(form, ['address', 'endereco'])
      const neighborhoodField = this.findFieldInForm(form, ['neighborhood', 'bairro'])
      const cityField = this.findFieldInForm(form, ['city', 'cidade'])
      const stateField = this.findFieldInForm(form, ['state', 'estado'])
      
      // Adiciona evento de blur para buscar o CEP
      if (this.hasZipCodeTarget) {
        this.createStatusIcon()
        this.zipCodeTarget.addEventListener('blur', () => {
          this.fetchAddress()
        })
        this.zipCodeTarget.addEventListener('input', () => {
          this.validateZipCode()
        })
      }
    }
  }

  /**
   * Cria o ícone de status que será mostrado ao lado do campo de CEP
   * @private
   */
  createStatusIcon() {
    // Se já existe um wrapper, não cria novamente
    if (this.zipCodeTarget.parentNode.classList.contains('viacep-wrapper')) {
      return
    }

    // Remove qualquer ícone existente
    const existingIcon = this.element.querySelector('.viacep-status-icon')
    if (existingIcon) existingIcon.remove()

    // Cria o novo ícone
    const icon = document.createElement('span')
    icon.className = 'viacep-status-icon'
    icon.style.cssText = `
      position: absolute;
      right: 10px;
      top: 50%;
      transform: translateY(-50%);
      font-size: 1.2em;
      display: none;
    `

    // Adiciona o ícone após o campo de CEP
    const wrapper = document.createElement('div')
    wrapper.style.position = 'relative'
    wrapper.className = 'viacep-wrapper' // Adiciona uma classe para identificar o wrapper
    
    // Mantém os estilos do campo original
    const computedStyle = window.getComputedStyle(this.zipCodeTarget)
    wrapper.style.width = computedStyle.width
    wrapper.style.display = computedStyle.display
    
    this.zipCodeTarget.parentNode.insertBefore(wrapper, this.zipCodeTarget)
    wrapper.appendChild(this.zipCodeTarget)
    wrapper.appendChild(icon)
  }

  /**
   * Mostra o ícone de sucesso (✓) ao lado do campo de CEP
   * @private
   */
  showSuccessIcon() {
    const wrapper = this.zipCodeTarget.closest('.viacep-wrapper')
    const icon = wrapper?.querySelector('.viacep-status-icon')
    if (icon) {
      icon.textContent = '✓'
      icon.style.display = 'block'
      icon.style.color = '#28a745'
    }
  }

  /**
   * Mostra o ícone de erro (✗) ao lado do campo de CEP
   * @private
   */
  showErrorIcon() {
    const wrapper = this.zipCodeTarget.closest('.viacep-wrapper')
    const icon = wrapper?.querySelector('.viacep-status-icon')
    if (icon) {
      icon.textContent = '✗'
      icon.style.display = 'block'
      icon.style.color = '#dc3545'
    }
  }

  /**
   * Esconde o ícone de status
   * @private
   */
  hideStatusIcon() {
    const wrapper = this.zipCodeTarget.closest('.viacep-wrapper')
    const icon = wrapper?.querySelector('.viacep-status-icon')
    if (icon) {
      icon.style.display = 'none'
    }
  }

  /**
   * Busca o endereço na API do ViaCEP
   * Este método é chamado quando o campo de CEP perde o foco
   * @async
   */
  async fetchAddress() {
    if (!this.hasZipCodeTarget) {      
      return
    }
    
    const zipCode = this.zipCodeTarget.value.replace(/\D/g, '') // Remove caracteres não numéricos
    
    if (zipCode.length !== 8) {
      this.clearFields()
      this.showErrorIcon()
      return
    }

    this.showLoading()
    this.hideStatusIcon()

    try {
      const response = await fetch(`https://viacep.com.br/ws/${zipCode}/json/`)
      const data = await response.json()

      if (data.erro) {
        this.showError("CEP não encontrado")
        this.showErrorIcon()
        this.clearFields()
        return
      }

      this.fillFields(data)
      this.showSuccess("Endereço encontrado!")
      this.showSuccessIcon()
      
    } catch (error) {
      console.error("❌ Erro ao consultar ViaCEP:", error)
      this.showError("Erro ao consultar CEP")
      this.showErrorIcon()
      this.clearFields()
    }
  }

  /**
   * Preenche os campos de endereço com os dados retornados pela API
   * @param {Object} data - Dados retornados pela API do ViaCEP
   * @param {string} data.logradouro - Nome da rua
   * @param {string} data.complemento - Complemento do endereço
   * @param {string} data.bairro - Nome do bairro
   * @param {string} data.localidade - Nome da cidade
   * @param {string} data.uf - Sigla do estado
   */
  fillFields(data) {    
    // Busca campos relacionados no formulário (busca no contexto do form pai)
    const form = this.zipCodeTarget.closest('form')
    
    // Busca campos de endereço no formulário
    const addressField = this.findFieldInForm(form, ['address', 'endereco'])
    const neighborhoodField = this.findFieldInForm(form, ['neighborhood', 'bairro'])
    const cityField = this.findFieldInForm(form, ['city', 'cidade'])
    const stateField = this.findFieldInForm(form, ['state', 'estado'])
    
    // Preenche endereço (apenas logradouro e complemento, sem bairro)
    if (addressField) {
      const addressParts = []
      
      if (data.logradouro) addressParts.push(data.logradouro)
      if (data.complemento) addressParts.push(data.complemento)
      
      const fullAddress = addressParts.join(', ')
      
      this.fillField(addressField, fullAddress)
    }

    // Preenche bairro em campo separado
    if (neighborhoodField) {
      this.fillField(neighborhoodField, data.bairro || '')
    }

    // Preenche cidade
    if (cityField) {
      this.fillField(cityField, data.localidade || '')
    }

    // Preenche estado
    if (stateField) {
      this.fillField(stateField, data.uf || '')
    }

    // Adiciona dados extras como atributos data para uso posterior se necessário
    this.zipCodeTarget.dataset.ibge = data.ibge || ''
    this.zipCodeTarget.dataset.ddd = data.ddd || ''
    this.zipCodeTarget.dataset.gia = data.gia || ''
    this.zipCodeTarget.dataset.siafi = data.siafi || ''
  }

  /**
   * Procura um campo no formulário pelo nome
   * Suporta três formatos de busca:
   * 1. Data attribute personalizado: data-viacep-field="nome_do_campo"
   * 2. Formato padrão Rails: input[name*="[nome_do_campo]"]
   * 3. Dentro de divs: [id*="nome_do_campo_input"]
   * 
   * @param {HTMLFormElement} form - O formulário onde buscar os campos
   * @param {Array<string>} fieldNames - Lista de possíveis nomes do campo
   * @returns {HTMLInputElement|HTMLTextAreaElement|null} O campo encontrado ou null
   */
  findFieldInForm(form, fieldNames) {
    for (const name of fieldNames) {
      // Primeiro tenta encontrar campos com data-viacep-field
      const customField = form.querySelector(`[data-viacep-field="${name}"]`)
      if (customField) {
        return customField
      }

      // Busca por input/textarea com name ou id que contenha o nome do campo
      const input = form.querySelector(`
        input[name*="[${name}]"],
        input[id*="${name}"],
        textarea[name*="[${name}]"],
        textarea[id*="${name}"]
      `)
      if (input) {
        return input
      }

      // Se não encontrar input direto, busca dentro da div do campo
      const fieldDiv = form.querySelector(`[id*="${name}_input"]`)
      if (fieldDiv) {
        const input = fieldDiv.querySelector('input, textarea')
        if (input) {
          return input
        }
      }
    }
    return null
  }

  /**
   * Preenche um campo com um valor e dispara os eventos necessários
   * @param {HTMLInputElement|HTMLTextAreaElement} field - O campo a ser preenchido
   * @param {string} value - O valor a ser inserido no campo
   */
  fillField(field, value) {
    if (!field) return

    // Remove readonly e placeholder temporariamente
    field.removeAttribute('readonly')
    const oldPlaceholder = field.placeholder
    field.removeAttribute('placeholder')
    
    // Define o valor
    field.value = value
    
    // Dispara eventos para garantir que o formulário seja atualizado
    field.dispatchEvent(new Event('input', { bubbles: true }))
    field.dispatchEvent(new Event('change', { bubbles: true }))
    
    // Restaura o placeholder
    if (oldPlaceholder) {
      field.placeholder = oldPlaceholder
    }
  }

  /**
   * Limpa todos os campos de endereço
   */
  clearFields() {
    const form = this.zipCodeTarget.closest('form')
    
    const addressField = this.findFieldInForm(form, ['address', 'endereco'])
    const neighborhoodField = this.findFieldInForm(form, ['neighborhood', 'bairro'])
    const cityField = this.findFieldInForm(form, ['city', 'cidade'])
    const stateField = this.findFieldInForm(form, ['state', 'estado'])
    
    // Limpa todos os campos e os torna editáveis para preenchimento manual
    if (addressField) {
      addressField.value = ''
      addressField.removeAttribute('readonly')
      addressField.style.backgroundColor = ''
      addressField.style.color = ''
      addressField.style.cursor = ''
      addressField.placeholder = 'Digite o endereço manualmente'
    }

    if (neighborhoodField) {
      neighborhoodField.value = ''
      neighborhoodField.removeAttribute('readonly')
      neighborhoodField.style.backgroundColor = ''
      neighborhoodField.style.color = ''
      neighborhoodField.style.cursor = ''
      neighborhoodField.placeholder = 'Digite o bairro manualmente'
    }

    if (cityField) {
      cityField.value = ''
      cityField.removeAttribute('readonly')
      cityField.style.backgroundColor = ''
      cityField.style.color = ''
      cityField.style.cursor = ''
      cityField.placeholder = 'Digite a cidade manualmente'
    }

    if (stateField) {
      stateField.value = ''
      stateField.removeAttribute('readonly')
      stateField.style.backgroundColor = ''
      stateField.style.color = ''
      stateField.style.cursor = ''
      stateField.placeholder = 'Digite o estado manualmente'
    }
  }

  /**
   * Mostra o estado de loading no campo de CEP
   */
  showLoading() {
    // Adiciona classe de loading no campo CEP
    this.zipCodeTarget.classList.add('loading')
  }

  /**
   * Mostra uma mensagem de erro no toast
   * @param {string} message - Mensagem de erro
   */
  showError(message) {
    this.showToast(message, 'error')
    this.zipCodeTarget.classList.remove('loading')
  }

  /**
   * Mostra uma mensagem de sucesso no toast
   * @param {string} message - Mensagem de sucesso
   */
  showSuccess(message) {
    this.showToast(message, 'success')
    this.zipCodeTarget.classList.remove('loading')
  }

  /**
   * Mostra uma mensagem no toast
   * @param {string} message - Mensagem a ser exibida
   * @param {string} type - Tipo da mensagem (success ou error)
   */
  showToast(message, type) {
    const toastContainer = document.getElementById('toast-container') || this.createToastContainer()
    
    const toast = document.createElement('div')
    toast.className = `toast toast-${type}`
    toast.textContent = message
    
    toastContainer.appendChild(toast)
    
    // Remove o toast após 3 segundos
    setTimeout(() => {
      toast.remove()
      if (toastContainer.children.length === 0) {
        toastContainer.remove()
      }
    }, 3000)
  }

  /**
   * Cria o container para os toasts
   * @returns {HTMLElement} O container criado
   */
  createToastContainer() {
    const container = document.createElement('div')
    container.id = 'toast-container'
    document.body.appendChild(container)
    return container
  }

  /**
   * Valida o formato do CEP em tempo real
   * @private
   */
  validateZipCode() {
    if (!this.hasZipCodeTarget) return

    const zipCode = this.zipCodeTarget.value.replace(/\D/g, '')
    
    if (zipCode.length === 0) {
      this.hideStatusIcon()
    } else if (zipCode.length === 8) {
      // Não mostra nenhum ícone ainda, aguarda a busca
      this.hideStatusIcon()
    } else {
      this.showErrorIcon()
    }
  }
} 